import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CompaniesList, CompanyCreationApi } from "./companiesApi";
import { QueryStatus, authAxios } from "../../../utils";
import { RootState } from "../../../redux/store";
import { loadCompanyAsync } from "../homeSlice";

export interface CompaniesState {
  companyCreationStatus: QueryStatus;
  companies: CompaniesList[];
  activeCompanies: CompaniesList[];
  loadAllCompaniesStatus: QueryStatus;
  updateCompanyStatusStatus: QueryStatus;
}

const initialState: CompaniesState = {
  companyCreationStatus: "idle",
  companies: [],
  activeCompanies: [],
  loadAllCompaniesStatus: "idle",
  updateCompanyStatusStatus: "idle",
};

export const loadAllCompaniesAsync = createAsyncThunk(
  "loadAllCompanies/call",
  async (_, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const manager = (thunkAPI.getState() as RootState).home.manager;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.get<CompaniesList[]>(
      `companies/manager/${manager?.uuid}`,
    );
    const res = response.data.sort((a, b) => a.name.localeCompare(b.name));
    void thunkAPI.dispatch(
      companiesSlice.actions.setAllActiveCompanies(
        res.filter((company) => company.providerDeletedAt === null),
      ),
    );
    return res;
  },
);

export const companyAsync = createAsyncThunk(
  "companies/creation",
  async (payload: CompanyCreationApi, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    const response = await axios.post<{ uuid: string }>(`/company`, payload);
    void thunkAPI.dispatch(loadCompanyAsync({ uuid: response.data.uuid }));
    void thunkAPI.dispatch(loadAllCompaniesAsync());
  },
);

export const updateCompanyStatusAsync = createAsyncThunk(
  "companies/updateStatus",
  async (payload: { status: string }, thunkAPI) => {
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });
    await axios.post(`/company/${company?.uuid}/update_status`, payload);
  },
);

export const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {
    resetCompanyCreationStatus: (state) => {
      state.companyCreationStatus = "idle";
    },
    resetUpdateCompanyStatusStatus: (state) => {
      state.updateCompanyStatusStatus = "idle";
    },
    setAllActiveCompanies: (state, action: PayloadAction<CompaniesList[]>) => {
      state.activeCompanies = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(companyAsync.pending, (state) => {
        state.companyCreationStatus = "processing";
      })
      .addCase(companyAsync.fulfilled, (state) => {
        state.companyCreationStatus = "success";
      })
      .addCase(companyAsync.rejected, (state) => {
        state.companyCreationStatus = "failed";
      })
      .addCase(loadAllCompaniesAsync.pending, (state) => {
        state.loadAllCompaniesStatus = "processing";
      })
      .addCase(loadAllCompaniesAsync.fulfilled, (state, action) => {
        state.loadAllCompaniesStatus = "success";
        state.companies = action.payload;
      })
      .addCase(loadAllCompaniesAsync.rejected, (state) => {
        state.loadAllCompaniesStatus = "failed";
      })
      .addCase(updateCompanyStatusAsync.pending, (state) => {
        state.updateCompanyStatusStatus = "processing";
      })
      .addCase(updateCompanyStatusAsync.fulfilled, (state) => {
        state.updateCompanyStatusStatus = "success";
      })
      .addCase(updateCompanyStatusAsync.rejected, (state) => {
        state.updateCompanyStatusStatus = "failed";
      });
  },
});

export const selectCompanyCreationStatus = (state: RootState) =>
  state.companies.companyCreationStatus;
export const selectAllCompanies = (state: RootState) =>
  state.companies.companies;
export const selectAllCompaniesStatus = (state: RootState) =>
  state.companies.loadAllCompaniesStatus;
export const selectUpdateCompanyStatusStatus = (state: RootState) =>
  state.companies.updateCompanyStatusStatus;
export const selectAllActiveCompanies = (state: RootState) =>
  state.companies.activeCompanies;
export default companiesSlice.reducer;
